/**
 * Mobile Responsiveness Validation Utility
 * 
 * Comprehensive utility for validating mobile responsiveness across all features
 * including touch interactions, navigation patterns, form validation, file uploads,
 * dashboard layouts, performance metrics, and cross-device compatibility.
 */

/**
 * Mobile breakpoints for responsive testing
 */
export const MOBILE_BREAKPOINTS = {
  SMALL_MOBILE: { width: 320, height: 568, name: 'iPhone SE' },
  MOBILE: { width: 375, height: 667, name: 'iPhone 8' },
  LARGE_MOBILE: { width: 414, height: 896, name: 'iPhone 11 Pro Max' },
  TABLET: { width: 768, height: 1024, name: 'iPad' },
  TABLET_LANDSCAPE: { width: 1024, height: 768, name: 'iPad Landscape' }
};

/**
 * Device orientations for testing
 */
export const ORIENTATIONS = {
  PORTRAIT: 'portrait',
  LANDSCAPE: 'landscape'
};

/**
 * Touch interaction types to validate
 */
export const TOUCH_INTERACTIONS = {
  TAP: 'tap',
  DOUBLE_TAP: 'doubleTap',
  LONG_PRESS: 'longPress',
  SWIPE: 'swipe',
  PINCH: 'pinch',
  SCROLL: 'scroll'
};

/**
 * Validates if current viewport is mobile size
 */
export const isMobileViewport = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
};

/**
 * Validates if device supports touch
 */
export const isTouchDevice = () => {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * Gets current device orientation
 */
export const getDeviceOrientation = () => {
  if (typeof window === 'undefined') return ORIENTATIONS?.PORTRAIT;
  return window.innerWidth > window.innerHeight ? ORIENTATIONS?.LANDSCAPE : ORIENTATIONS?.PORTRAIT;
};

/**
 * Validates touch target size (minimum 44x44 pixels for accessibility)
 */
export const validateTouchTargetSize = (element) => {
  if (!element) return { valid: false, reason: 'Element not found' };
  
  const rect = element?.getBoundingClientRect();
  const minSize = 44; // Apple Human Interface Guidelines
  
  const valid = rect?.width >= minSize && rect?.height >= minSize;
  
  return {
    valid,
    width: rect?.width,
    height: rect?.height,
    reason: valid ? 'Touch target meets minimum size requirements' : `Touch target too small: ${rect?.width}x${rect?.height}px (minimum: ${minSize}x${minSize}px)`
  };
};

/**
 * Validates element visibility in viewport
 */
export const isElementVisibleInViewport = (element) => {
  if (!element) return false;
  
  const rect = element?.getBoundingClientRect();
  return (rect?.top >= 0 &&
  rect?.left >= 0 &&
  rect?.bottom <= (window.innerHeight || document.documentElement?.clientHeight) && rect?.right <= (window.innerWidth || document.documentElement?.clientWidth));
};

/**
 * Validates form field usability on mobile
 */
export const validateMobileFormField = (fieldElement) => {
  if (!fieldElement) return { valid: false, issues: ['Field element not found'] };
  
  const issues = [];
  const rect = fieldElement?.getBoundingClientRect();
  
  // Check minimum height for easy tapping
  if (rect?.height < 44) {
    issues?.push(`Input height too small: ${rect?.height}px (minimum: 44px)`);
  }
  
  // Check if field has proper label
  const label = document.querySelector(`label[for="${fieldElement?.id}"]`);
  if (!label && !fieldElement?.getAttribute('aria-label')) {
    issues?.push('Missing accessible label');
  }
  
  // Check if field has proper input mode
  const inputMode = fieldElement?.getAttribute('inputmode');
  if (fieldElement?.type === 'email' && inputMode !== 'email') {
    issues?.push('Missing inputmode="email" for email field');
  }
  if (fieldElement?.type === 'tel' && inputMode !== 'tel') {
    issues?.push('Missing inputmode="tel" for phone field');
  }
  if (fieldElement?.type === 'number' && !inputMode) {
    issues?.push('Missing inputmode for numeric field');
  }
  
  // Check if field has autocomplete
  if (!fieldElement?.getAttribute('autocomplete')) {
    issues?.push('Missing autocomplete attribute');
  }
  
  return {
    valid: issues?.length === 0,
    issues,
    recommendations: issues?.length > 0 ? generateFormFieldRecommendations(issues) : []
  };
};

/**
 * Generates recommendations for form field issues
 */
const generateFormFieldRecommendations = (issues) => {
  const recommendations = [];
  
  if (issues?.some(i => i?.includes('height'))) {
    recommendations?.push('Increase input field height to at least 44px');
  }
  if (issues?.some(i => i?.includes('label'))) {
    recommendations?.push('Add aria-label or associate with a label element');
  }
  if (issues?.some(i => i?.includes('inputmode'))) {
    recommendations?.push('Add appropriate inputmode attribute for better mobile keyboard');
  }
  if (issues?.some(i => i?.includes('autocomplete'))) {
    recommendations?.push('Add autocomplete attribute to help users fill forms faster');
  }
  
  return recommendations;
};

/**
 * Validates file upload functionality on mobile
 */
export const validateMobileFileUpload = (uploadElement) => {
  if (!uploadElement) return { valid: false, issues: ['Upload element not found'] };
  
  const issues = [];
  
  // Check if accept attribute is set
  if (!uploadElement?.getAttribute('accept')) {
    issues?.push('Missing accept attribute to specify allowed file types');
  }
  
  // Check if capture attribute is set for mobile camera access
  const accept = uploadElement?.getAttribute('accept');
  if (accept && accept?.includes('image') && !uploadElement?.getAttribute('capture')) {
    issues?.push('Consider adding capture="environment" to enable direct camera access');
  }
  
  // Check if multiple files are allowed when appropriate
  if (!uploadElement?.hasAttribute('multiple') && accept && accept?.includes('image')) {
    issues?.push('Consider allowing multiple file selection for better UX');
  }
  
  return {
    valid: issues?.length === 0,
    issues,
    recommendations: issues?.length > 0 ? [
      'Add accept attribute to filter file types',
      'Add capture attribute for camera access on mobile',
      'Consider enabling multiple file selection'
    ] : []
  };
};

/**
 * Validates navigation accessibility on mobile
 */
export const validateMobileNavigation = (navElement) => {
  if (!navElement) return { valid: false, issues: ['Navigation element not found'] };
  
  const issues = [];
  
  // Check for hamburger menu on mobile
  if (isMobileViewport()) {
    const hamburger = navElement?.querySelector('[aria-label*="menu"]') || 
                     navElement?.querySelector('.menu-toggle');
    if (!hamburger) {
      issues?.push('Missing hamburger menu for mobile navigation');
    }
  }
  
  // Check for proper ARIA labels
  if (!navElement?.getAttribute('aria-label')) {
    issues?.push('Missing aria-label on navigation element');
  }
  
  // Check for keyboard accessibility
  const links = navElement?.querySelectorAll('a, button');
  links?.forEach((link, index) => {
    if (!link?.getAttribute('tabindex') && link?.tabIndex === -1) {
      issues?.push(`Navigation item ${index + 1} is not keyboard accessible`);
    }
  });
  
  return {
    valid: issues?.length === 0,
    issues,
    recommendations: issues?.length > 0 ? [
      'Implement collapsible hamburger menu for mobile',
      'Add proper ARIA labels for screen readers',
      'Ensure all navigation items are keyboard accessible'
    ] : []
  };
};

/**
 * Validates dashboard layout responsiveness
 */
export const validateDashboardLayout = (dashboardElement) => {
  if (!dashboardElement) return { valid: false, issues: ['Dashboard element not found'] };
  
  const issues = [];
  
  // Check if layout uses responsive grid
  const computedStyle = window.getComputedStyle(dashboardElement);
  if (!computedStyle?.display?.includes('grid') && !computedStyle?.display?.includes('flex')) {
    issues?.push('Dashboard should use CSS Grid or Flexbox for responsive layout');
  }
  
  // Check for horizontal scrolling
  if (dashboardElement?.scrollWidth > dashboardElement?.clientWidth) {
    issues?.push('Dashboard has horizontal overflow on mobile');
  }
  
  // Check card sizes
  const cards = dashboardElement?.querySelectorAll('[class*="card"], [class*="Card"]');
  cards?.forEach((card, index) => {
    const cardRect = card?.getBoundingClientRect();
    if (cardRect?.width > window.innerWidth) {
      issues?.push(`Card ${index + 1} exceeds viewport width`);
    }
  });
  
  return {
    valid: issues?.length === 0,
    issues,
    recommendations: issues?.length > 0 ? [
      'Use responsive grid layout (CSS Grid or Flexbox)',
      'Ensure cards stack vertically on mobile',
      'Add proper spacing and padding for mobile'
    ] : []
  };
};

/**
 * Tests touch interactions on interactive elements
 */
export const testTouchInteractions = (containerElement) => {
  if (!containerElement) return { passedTests: 0, totalTests: 0, tests: [] };
  
  const tests = [];
  let passedTests = 0;
  
  // Test buttons
  const buttons = containerElement?.querySelectorAll('button, [role="button"]');
  buttons?.forEach((button, index) => {
    const validation = validateTouchTargetSize(button);
    const test = {
      name: `Button ${index + 1} Touch Target`,
      description: 'Validates button meets minimum touch target size',
      passed: validation?.valid,
      issues: validation?.valid ? [] : [validation?.reason]
    };
    tests?.push(test);
    if (test?.passed) passedTests++;
  });
  
  // Test links
  const links = containerElement?.querySelectorAll('a');
  links?.forEach((link, index) => {
    const validation = validateTouchTargetSize(link);
    const test = {
      name: `Link ${index + 1} Touch Target`,
      description: 'Validates link meets minimum touch target size',
      passed: validation?.valid,
      issues: validation?.valid ? [] : [validation?.reason]
    };
    tests?.push(test);
    if (test?.passed) passedTests++;
  });
  
  // Test form inputs
  const inputs = containerElement?.querySelectorAll('input, textarea, select');
  inputs?.forEach((input, index) => {
    const validation = validateMobileFormField(input);
    const test = {
      name: `Form Field ${index + 1} Usability`,
      description: 'Validates form field is mobile-friendly',
      passed: validation?.valid,
      issues: validation?.issues || []
    };
    tests?.push(test);
    if (test?.passed) passedTests++;
  });
  
  return {
    passedTests,
    totalTests: tests?.length,
    tests,
    score: tests?.length > 0 ? Math.round((passedTests / tests?.length) * 100) : 0
  };
};

/**
 * Validates mobile performance metrics
 */
export const validateMobilePerformance = () => {
  if (typeof window === 'undefined' || !window.performance) {
    return { score: 0, loadTime: 0, recommendations: ['Performance API not available'] };
  }
  
  const navigation = performance.getEntriesByType('navigation')?.[0];
  const paint = performance.getEntriesByType('paint');
  
  const loadTime = navigation?.loadEventEnd - navigation?.fetchStart || 0;
  const firstPaint = paint?.find(p => p?.name === 'first-paint')?.startTime || 0;
  const firstContentfulPaint = paint?.find(p => p?.name === 'first-contentful-paint')?.startTime || 0;
  
  // Calculate scores
  const loadTimeScore = loadTime < 3000 ? 100 : loadTime < 5000 ? 70 : 40;
  const firstPaintScore = firstPaint < 1000 ? 100 : firstPaint < 2000 ? 70 : 40;
  const overallScore = Math.round((loadTimeScore + firstPaintScore) / 2);
  
  const recommendations = [];
  if (loadTime > 3000) recommendations?.push('Optimize bundle size and reduce load time');
  if (firstPaint > 1000) recommendations?.push('Improve first paint time with code splitting');
  if (firstContentfulPaint > 2000) recommendations?.push('Optimize critical rendering path');
  
  return {
    score: overallScore,
    loadTime: Math.round(loadTime),
    firstPaint: Math.round(firstPaint),
    firstContentfulPaint: Math.round(firstContentfulPaint),
    timeToInteractive: Math.round(navigation?.domInteractive - navigation?.fetchStart || 0),
    bundleSize: Math.round(performance?.memory?.usedJSHeapSize / 1024 || 0),
    loadTimeScore,
    firstPaintScore,
    interactiveScore: navigation?.domInteractive < 3000 ? 100 : 70,
    bundleSizeScore: performance?.memory?.usedJSHeapSize < 5000000 ? 100 : 70,
    recommendations
  };
};

/**
 * Tests cross-device compatibility
 */
export const testCrossDeviceCompatibility = () => {
  const devices = [];
  
  Object.entries(MOBILE_BREAKPOINTS)?.forEach(([key, value]) => {
    const issues = [];
    let score = 100;
    
    // Simulate viewport
    const currentWidth = window.innerWidth;
    const currentHeight = window.innerHeight;
    
    // Check if content fits
    if (document.body?.scrollWidth > value?.width) {
      issues?.push('Horizontal overflow detected');
      score -= 20;
    }
    
    // Check for fixed positioning issues
    const fixedElements = document.querySelectorAll('[style*="position: fixed"], [style*="position:fixed"]');
    if (fixedElements?.length > 0 && value?.width < 768) {
      issues?.push('Fixed positioning may cause issues on small screens');
      score -= 10;
    }
    
    // Check for viewport meta tag
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) {
      issues?.push('Missing viewport meta tag');
      score -= 30;
    }
    
    devices?.push({
      name: value?.name,
      type: key?.includes('TABLET') ? 'tablet' : 'mobile',
      viewport: `${value?.width}×${value?.height}`,
      score: Math.max(0, score),
      issues
    });
  });
  
  return {
    devices,
    overallScore: Math.round(devices?.reduce((sum, d) => sum + d?.score, 0) / devices?.length)
  };
};

/**
 * Performs comprehensive mobile audit
 */
export const performMobileAudit = (containerElement) => {
  if (!containerElement) {
    return {
      overallScore: 0,
      criticalIssues: ['Container element not found'],
      warnings: [],
      recommendations: [],
      passedTests: 0
    };
  }
  
  const criticalIssues = [];
  const warnings = [];
  const recommendations = [];
  let passedTests = 0;
  let totalTests = 0;
  
  // Test viewport configuration
  totalTests++;
  const viewportMeta = document.querySelector('meta[name="viewport"]');
  if (!viewportMeta) {
    criticalIssues?.push('Missing viewport meta tag - required for mobile responsiveness');
  } else {
    passedTests++;
  }
  
  // Test touch targets
  const touchTests = testTouchInteractions(containerElement);
  totalTests += touchTests?.totalTests;
  passedTests += touchTests?.passedTests;
  
  if (touchTests?.passedTests < touchTests?.totalTests * 0.8) {
    warnings?.push(`${touchTests?.totalTests - touchTests?.passedTests} touch targets need improvement`);
  }
  
  // Test navigation
  totalTests++;
  const nav = containerElement?.querySelector('nav, [role="navigation"]');
  if (nav) {
    const navValidation = validateMobileNavigation(nav);
    if (navValidation?.valid) {
      passedTests++;
    } else {
      warnings?.push(...navValidation?.issues);
      recommendations?.push(...navValidation?.recommendations);
    }
  }
  
  // Test forms
  const forms = containerElement?.querySelectorAll('form');
  forms?.forEach((form) => {
    const inputs = form?.querySelectorAll('input, textarea, select');
    inputs?.forEach((input) => {
      totalTests++;
      const validation = validateMobileFormField(input);
      if (validation?.valid) {
        passedTests++;
      } else {
        warnings?.push(...validation?.issues);
      }
    });
  });
  
  // Test horizontal overflow
  totalTests++;
  if (document.body?.scrollWidth > window.innerWidth) {
    criticalIssues?.push('Page has horizontal overflow - content exceeds viewport width');
  } else {
    passedTests++;
  }
  
  // Test text readability
  totalTests++;
  const textElements = containerElement?.querySelectorAll('p, span, div, h1, h2, h3, h4, h5, h6');
  let smallTextCount = 0;
  textElements?.forEach((el) => {
    const fontSize = parseFloat(window.getComputedStyle(el)?.fontSize);
    if (fontSize < 14) smallTextCount++;
  });
  
  if (smallTextCount > textElements?.length * 0.3) {
    warnings?.push('Many text elements have small font sizes (< 14px) - may be hard to read on mobile');
  } else {
    passedTests++;
  }
  
  // Calculate scores
  const overallScore = totalTests > 0 ? Math.round((passedTests / totalTests) * 100) : 0;
  const touchTargetCompliance = touchTests?.totalTests > 0 ? Math.round((touchTests?.passedTests / touchTests?.totalTests) * 100) : 100;
  
  // Generate recommendations
  if (overallScore < 70) {
    recommendations?.push('Consider implementing mobile-first design approach');
  }
  if (criticalIssues?.length > 0) {
    recommendations?.push('Fix critical issues immediately to ensure mobile functionality');
  }
  if (touchTargetCompliance < 80) {
    recommendations?.push('Increase touch target sizes to at least 44x44 pixels');
  }
  
  return {
    overallScore,
    passedTests,
    totalTests,
    criticalIssues,
    warnings,
    recommendations,
    touchTargetCompliance,
    viewportOptimization: viewportMeta ? 100 : 0,
    formUsability: totalTests > 0 ? Math.round((passedTests / totalTests) * 100) : 100,
    navigationAccessibility: nav ? (navValidation?.valid ? 100 : 70) : 50
  };
};

/**
 * Exports validation report as JSON
 */
export const exportValidationReport = (results) => {
  const report = {
    timestamp: new Date()?.toISOString(),
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      orientation: getDeviceOrientation()
    },
    userAgent: navigator.userAgent,
    touchSupport: isTouchDevice(),
    results
  };
  
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `mobile-responsiveness-report-${Date.now()}.json`;
  document.body?.appendChild(a);
  a?.click();
  document.body?.removeChild(a);
  URL.revokeObjectURL(url);
};

export default {
  MOBILE_BREAKPOINTS,
  ORIENTATIONS,
  TOUCH_INTERACTIONS,
  isMobileViewport,
  isTouchDevice,
  getDeviceOrientation,
  validateTouchTargetSize,
  isElementVisibleInViewport,
  validateMobileFormField,
  validateMobileFileUpload,
  validateMobileNavigation,
  validateDashboardLayout,
  testTouchInteractions,
  validateMobilePerformance,
  testCrossDeviceCompatibility,
  performMobileAudit,
  exportValidationReport
};